import NextImage from 'components/elements/NextImage'
import { ComponentContainer } from 'components/layouts/Layouts.css'
import ReviewsSection from 'components/modules/LandingPage/ReviewsSection'
import {
    AnalyticsEventUploadAppFeaturedStoryButtonClick,
    AnalyticsEventUploadAppFeaturedStoryImpression,
} from 'lib/constants/AnalyticsEventType'
import _useTranslation from 'lib/hooks/_useTranslation'
import useImpressionTracking from 'lib/hooks/useImpressionTracking'
import { useRef } from 'react'
import { Headline3, Headline4 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled from 'styled-components'
import FeaturedStoryImage from 'assets/expansion-process/featured-story.png'
import AppHomescreen from 'assets/know/stylized-homescreen.webp'
import Row from 'components/elements/Row'
import DownloadButton from 'components/modules/SEO/DownloadBanner'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'
import { trackEvent } from 'lib/tracking/analytics-service'
import { Know } from 'interfaces/Know'
import { KnowunityNumbers } from 'lib/constants/KnowunityNumbers'
import StatisticsGrid from 'components/modules/LandingPage/StatisticsGrid'
import Stack from 'components/elements/Stack'

const FeaturedStoryWrapper = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 50px;
    min-height: 800px;
    place-content: center;
    background-color: ${theme.colors.transparent};
    padding: 25px;

    .texts {
        display: flex;
        flex-direction: column;
        justify-content: center;
        gap: 25px;
    }
    @media (max-width: 1050px) {
        display: flex;
        flex-direction: column;
        align-items: center;
        .app {
            padding: 15px 0;
        }
        .texts {
            padding: 0px 5% 0 5%;
            align-items: center;
            * {
                text-align: center;
            }
        }
    }
    .app-featured-story-image {
        filter: brightness(0) invert(1);
    }
`

interface Props {
    isLaunched: boolean
    referrerScreen?: string
    know?: Know
    isDarkTheme?: boolean
}

const AppFeaturedStory = ({ referrerScreen, isLaunched, know, isDarkTheme }: Props) => {
    const { t } = _useTranslation('knows')
    const lg = useMedia(deviceSize.lg)
    const ref = useRef<HTMLDivElement>(null)

    useImpressionTracking(ref, AnalyticsEventUploadAppFeaturedStoryImpression, { referrerScreen, know })

    const statisticsInfo = {
        text_color: isDarkTheme ? theme.colors.white : theme.colors.businessGray,
        background_color: isDarkTheme ? `${theme.colors.darkThemePrimary}99` : `#d0def2`,
        image: {
            url: '',
            alt: '',
            dimensions: {
                width: 0,
                height: 0,
            },
        },
        image_link: '',
        title_1: `${KnowunityNumbers.appleReviewRating}+`,
        title_2: `${KnowunityNumbers.downloadMillionCount} M`,
        title_3: '#1',
        title_4: `${KnowunityNumbers.knowThousandsCount} K+`,
        subtitle_1: t('knows/expansionStatisticsAppRating'),
        subtitle_2: t('knows/expansionStatisticsStudentCount'),
        subtitle_3: t('knows/expansionStatisticsCountriesCount', { countries: KnowunityNumbers.activeCountryCount }),
        subtitle_4: t('knows/expansionStatisticsStudyNotes'),
    }

    const reviews = [
        {
            text: t('knows/expansionReviewText1'),
            user: t('knows/expansionReviewUser1'),
        },
        {
            text: t('knows/expansionReviewText2'),
            user: t('knows/expansionReviewUser2'),
        },
        {
            text: t('knows/expansionReviewText3'),
            user: t('knows/expansionReviewUser3'),
        },
    ]

    const onAppFeaturedButtonClicked = (type: 'android' | 'ios') => {
        trackEvent(AnalyticsEventUploadAppFeaturedStoryButtonClick, { referrerScreen, value: type, know })
    }

    return (
        <>
            <Stack gutter={0}>
                <ComponentContainer
                    color={isDarkTheme ? theme.colors.darkThemeSecondary : theme.colors.snowWhite}
                    fitContent
                >
                    <section>
                        <FeaturedStoryWrapper ref={ref}>
                            <div className="texts">
                                <Headline3
                                    fontSize={30}
                                    fontFamily="var(--font-poppins)"
                                    fontWeight={600}
                                    color={isDarkTheme ? theme.colors.white : theme.colors.headlineDark}
                                >
                                    {t('knows/expansionRankedInEducation')}
                                </Headline3>
                                <Headline4
                                    fontFamily="var(--font-inter)"
                                    fontWeight={400}
                                    fontSize={16}
                                    color={isDarkTheme ? theme.colors.white : theme.colors.contentSecondary}
                                >
                                    {t('knows/expansionRankedInEducationText')}
                                </Headline4>

                                <NextImage
                                    src={FeaturedStoryImage.src}
                                    width={179}
                                    height={81}
                                    alt="Ranked #1 Education App"
                                    className="app-featured-story-image"
                                />
                                {lg ? (
                                    <NextImage
                                        className="app"
                                        src={AppHomescreen.src}
                                        width={360}
                                        height={360}
                                        alt={t('knows/expansionRankedInEducation')}
                                    />
                                ) : null}
                                <Row>
                                    {isLaunched ? (
                                        <>
                                            <DownloadButton
                                                onClick={() => onAppFeaturedButtonClicked('android')}
                                                type="android"
                                                referrerScreen="appFeaturedStory"
                                                isDark={!isDarkTheme}
                                                backgroundColor={isDarkTheme ? theme.colors.white : undefined}
                                            />
                                            <DownloadButton
                                                onClick={() => onAppFeaturedButtonClicked('ios')}
                                                type="ios"
                                                referrerScreen="appFeaturedStory"
                                                isDark={!isDarkTheme}
                                                backgroundColor={isDarkTheme ? theme.colors.white : undefined}
                                            />
                                        </>
                                    ) : null}
                                </Row>
                            </div>
                            {!lg ? (
                                <NextImage
                                    src={AppHomescreen.src}
                                    width={AppHomescreen.width}
                                    height={AppHomescreen.height}
                                    alt={t('knows/expansionRankedInEducation')}
                                    quality={100}
                                />
                            ) : null}
                        </FeaturedStoryWrapper>
                    </section>
                </ComponentContainer>

                <StatisticsGrid data={statisticsInfo} />
                <ComponentContainer
                    color={isDarkTheme ? theme.colors.darkThemeSecondary : theme.colors.snowWhite}
                    fitContent
                >
                    <section>
                        <Headline3
                            fontSize={22}
                            fontWeight={600}
                            color={isDarkTheme ? theme.colors.white : theme.colors.businessGray}
                        >
                            {t('knows/expansionReviewsTitle')}
                        </Headline3>

                        <ReviewsSection items={reviews} isScrollable />
                    </section>
                </ComponentContainer>
            </Stack>
        </>
    )
}

export default AppFeaturedStory
